import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Film, FolderOpen, Sparkles } from 'lucide-react'; // Using a more descriptive icon
import { transformKickThumbnailSize, extractVodInfo } from '../../utils/thumbnails';

interface JobClipGroupProps {
  jobId: string;
  vodTitle: string | null;
  vodThumbnailUrl: string | null;
  vodUrl: string | null;
  platform?: string | null;
  clipCount: number;
  hasEditedClips?: boolean;
  activeCategory?: 'original' | 'edited'; // Yeni prop
}

const JobClipGroup: React.FC<JobClipGroupProps> = ({ jobId, vodTitle, vodThumbnailUrl, vodUrl, platform, clipCount, hasEditedClips, activeCategory }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleCardClick = () => {
    // Sadece Creations sekmesindeyse edited filtresini aç
    const isFromCreations = activeCategory === 'edited';
    
    if (isFromCreations) {
      navigate(`/clips/${jobId}`, { 
        state: { 
          fromCreations: true,
          category: 'edited' 
        } 
      });
    } else {
      navigate(`/clips/${jobId}`);
    }
  };

  // Extract platform info and apply platform-specific thumbnail transformations
  const { detectedPlatform, displayThumbnailUrl } = React.useMemo(() => {
    // Use provided platform, or fall back to URL parsing
    const platformValue = platform || (vodUrl ? extractVodInfo(vodUrl).platform : 'unknown');
    let thumbnailUrl = vodThumbnailUrl;
    
    if (!vodThumbnailUrl) {
      return { detectedPlatform: platformValue, displayThumbnailUrl: null };
    }
    
    // For platform-specific processing
    if (platformValue === 'kick') {
      // If it's a cached URL, use it directly
      if (vodThumbnailUrl.includes('/api/thumbnails/cached/')) {
        thumbnailUrl = vodThumbnailUrl;
      } else {
        thumbnailUrl = transformKickThumbnailSize(vodThumbnailUrl, 320);
      }
    } else {
      // For other platforms or generic thumbnails, use the existing size replacement
      thumbnailUrl = vodThumbnailUrl.replace(/%\{width\}/g, '320').replace(/%\{height\}/g, '180');
    }
    
    return { detectedPlatform: platformValue, displayThumbnailUrl: thumbnailUrl };
  }, [vodThumbnailUrl, vodUrl, platform]);

  return (
    <div className="relative group cursor-pointer">
      {/* Background gradient glow */}
      <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-cyan-500/20 rounded-2xl opacity-0 blur-xl group-hover:opacity-40 transition-opacity duration-300" />
      
      <div
        className="
          relative bg-black/30 backdrop-blur-xl border border-white/10 
          rounded-2xl overflow-hidden shadow-lg
          hover:bg-black/40 hover:border-white/20 transition-all duration-300
          transform hover:scale-[1.02]
        "
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        aria-label={`View clips for ${vodTitle || 'this VOD'}`}
      >
        {/* Thumbnail Section */}
        <div className="relative aspect-video w-full bg-black/20">
          {displayThumbnailUrl ? (
            <img
              src={displayThumbnailUrl}
              alt="VOD Thumbnail"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Film className="w-16 h-16 text-white/40" />
            </div>
          )}
          
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          
          {/* Platform and clip count badges */}
          <div className="absolute top-3 right-3 flex gap-2">
            {/* Platform badge */}
            {detectedPlatform !== 'unknown' && (
              <div className={`
                backdrop-blur-sm border rounded-full px-2 py-1
                ${detectedPlatform === 'kick' 
                  ? 'bg-green-900/60 border-green-400/40 text-green-300' 
                  : 'bg-purple-900/60 border-purple-400/40 text-purple-300'
                }
              `}>
                <span className="text-xs font-medium capitalize">{detectedPlatform}</span>
              </div>
            )}
            
            {/* Edited clips badge - sadece Creations tabındayken göster */}
            {hasEditedClips && activeCategory === 'edited' && (
              <div className="bg-gradient-to-r from-purple-500/80 to-pink-500/80 backdrop-blur-sm border border-purple-400/40 rounded-full px-2 py-1 flex items-center gap-1">
                <Sparkles size={10} className="text-white" />
                <span className="text-xs font-medium text-white">EDITED</span>
              </div>
            )}
            
            {/* Clip count badge */}
            <div className="bg-black/60 backdrop-blur-sm border border-white/20 rounded-full px-3 py-1 flex items-center gap-2">
              <FolderOpen size={14} className="text-white/80" />
              <span className="text-xs font-medium text-white">{clipCount}</span>
            </div>
          </div>
        </div>
        
        {/* Content Section */}
        <div className="p-4">
          <h3 
            className="text-lg font-semibold text-white truncate group-hover:text-white transition-colors" 
            title={vodTitle || `VOD ID: ${jobId}`}
          >
            {vodTitle || `VOD ID: ${jobId}`}
          </h3>
          
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center text-sm text-white/60">
              <span>{clipCount === 1 ? '1 clip' : `${clipCount} clips`} available</span>
            </div>
            
            <div className="flex items-center text-white/40 group-hover:text-white/60 transition-colors">
              <span className="text-xs">View all →</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobClipGroup;